import { Link, useLocation } from "react-router-dom";
import { Menu, User, ShoppingBag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import SearchBar from "@/components/SearchBar";

const navLinks = [
  { name: "Collection", path: "/collection" },
  { name: "Send a Thought", path: "/collection?tag=Thought%20of%20You" },
  { name: "Memberships", path: "/memberships" },
  { name: "Bespoke", path: "/bespoke" },
  { name: "Events", path: "/events" },
  { name: "Floral Bar", path: "/floral-bar" },
  { name: "Manifesto", path: "/manifesto" },
  { name: "FAQ", path: "/faq" },
];

export default function Navbar({ onMenuClick }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { count, setIsOpen } = useCart();

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#b8966a]/10"
      data-testid="navbar"
    >
      <div className="container-custom flex items-center justify-between h-[72px]">
        {/* Logo */}
        <Link 
          to="/" 
          className="font-display text-xs tracking-[0.35em] uppercase text-[#f8f5f0] hover:text-[#b8966a] transition-colors"
          data-testid="nav-logo"
        >
          <span className="text-[#b8966a]">Fleuris</span> Noir
        </Link>

        {/* Desktop Navigation */}
        <ul className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`font-sans text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  location.pathname === link.path
                    ? "text-[#b8966a]"
                    : "text-[#a19d98] hover:text-[#b8966a]"
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/inquiries"
              className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#b8966a] hover:text-[#cfae82] transition-colors"
              data-testid="nav-link-inquiries"
            >
              Inquiries
            </Link>
          </li>
          <li>
            {!loading && (
              <Link
                to={user ? "/account" : "/auth"}
                className={`flex items-center gap-1.5 font-sans text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  location.pathname === "/account" || location.pathname === "/auth"
                    ? "text-[#b8966a]"
                    : "text-[#a19d98] hover:text-[#b8966a]"
                }`}
                data-testid="nav-link-account"
              >
                <User size={14} />
                {user ? "Account" : "Sign In"}
              </Link>
            )}
          </li>
          <li>
            <SearchBar />
          </li>
          <li>
            <button onClick={() => setIsOpen(true)} className="relative p-2 text-[#a19d98] hover:text-[#b8966a] transition-colors" data-testid="nav-cart-btn" aria-label="Cart">
              <ShoppingBag size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#b8966a] text-[#0c0c0c] text-[10px] font-sans flex items-center justify-center rounded-full">{count}</span>
              )}
            </button>
          </li>
        </ul>

        {/* Mobile: Cart + Menu */}
        <div className="flex lg:hidden items-center gap-2">
          <button
            onClick={() => setIsOpen(true)}
            className="relative p-2 text-[#f8f5f0] hover:text-[#b8966a] transition-colors"
            data-testid="nav-cart-btn-mobile"
            aria-label="Cart"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#b8966a] text-[#0c0c0c] text-[10px] font-sans flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </button>
          <button
            onClick={onMenuClick}
            className="p-2 text-[#f8f5f0] hover:text-[#b8966a] transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>
    </nav>
  );
}
